"use client"

import { ArrowDownThinIcon } from "@/app/icons/arrow-down"
import {
  CollectionSearchSort,
  CollectionSearchSortDirection,
} from "@anaconda/core/nft-collection/search/types"
import { Trans } from "@lingui/react/macro"
import { cn } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useCallback } from "react"
import { CollectionLeaderboardFiltersMoleculeReatom } from "../../../store.reatom"
import { useStatisticPeriodLabel } from "../../statistic-period/hooks"
import { ColumnType } from "./column-type"

const HEADER_PROPERTIES_BY_COLUMN_TYPE: Record<
  ColumnType,
  {
    Label: React.ComponentType<{ periodLabel: React.ReactNode }>
    sort?: CollectionSearchSort
    horizontalAlign: "left" | "right"
  }
> = {
  [ColumnType.Collection]: {
    Label: () => <Trans>Collection</Trans>,
    horizontalAlign: "left",
  },
  [ColumnType.Floor]: {
    Label: () => <Trans>Floor</Trans>,
    sort: CollectionSearchSort.FLOOR,
    horizontalAlign: "right",
  },
  [ColumnType.FloorChange]: {
    Label: ({ periodLabel }) => <Trans>Fl. Ch {periodLabel}</Trans>,
    sort: CollectionSearchSort.FLOOR_CHANGE,
    horizontalAlign: "right",
  },
  [ColumnType.TopOffer]: {
    Label: () => <Trans>Top Offer</Trans>,
    sort: CollectionSearchSort.TOP_OFFER,
    horizontalAlign: "right",
  },
  [ColumnType.Sales]: {
    Label: ({ periodLabel }) => <Trans>Sales {periodLabel}</Trans>,
    sort: CollectionSearchSort.SALES,
    horizontalAlign: "right",
  },
  [ColumnType.Owners]: {
    Label: () => <Trans>Owners</Trans>,
    sort: CollectionSearchSort.OWNERS,
    horizontalAlign: "right",
  },
  [ColumnType.Volume]: {
    Label: ({ periodLabel }) => <Trans>Volume {periodLabel}</Trans>,
    sort: CollectionSearchSort.VOLUME,
    horizontalAlign: "right",
  },
  [ColumnType.FloorChart]: {
    Label: ({ periodLabel }) => <Trans>Floor {periodLabel}</Trans>,
    sort: CollectionSearchSort.FLOOR_CHANGE,
    horizontalAlign: "right",
  },
}

export const ColumnHeader = reatomComponent(
  (props: {
    type: ColumnType
  }) => {
    const periodLabel = useStatisticPeriodLabel()
    const {
      Label,
      sort: columnSort,
      horizontalAlign,
    } = HEADER_PROPERTIES_BY_COLUMN_TYPE[props.type]

    const { sortBy, sortDirection, toggleSortBy } = useMolecule(
      CollectionLeaderboardFiltersMoleculeReatom,
    )

    const currentSortBy = sortBy()
    const currentSortDirection = sortDirection()

    const onClick = useCallback(() => {
      if (columnSort) {
        toggleSortBy(columnSort)
      }
    }, [columnSort, toggleSortBy])

    return (
      <div
        onClick={onClick}
        className={cn(
          "group flex items-center gap-[2px] text-foreground/40",
          columnSort && "cursor-pointer hover:text-foreground",
          horizontalAlign === "left" ? "justify-start" : "justify-end",
        )}
      >
        {columnSort === currentSortBy ? (
          <ArrowDownThinIcon
            className={cn(
              currentSortDirection === CollectionSearchSortDirection.ASC
                ? "rotate-180"
                : "",
            )}
          />
        ) : null}
        <span>
          <Label periodLabel={periodLabel} />
        </span>
      </div>
    )
  },
  "ColumnHeader",
)
